import React, { useRef, useEffect, useMemo, useState } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import { useNavigate, useLocation } from "react-router-dom";
import * as THREE from "three";

import "./App.css";
import Loader from "./Loader";
import WaveformPlayer from "./Waveform";
import Radar from "./Radar";
import SideMenu from "./SideMenu";
import TerminalFeed from "./TerminalFeed";
import TopNavigation from "./TopNavigation";
import FooterInfo from "./FooterInfo";
import LogoDisplay from "./LogoDisplay";
import BackgroundSVGs from "./BackgroundSVGs";
import CoolCursorTrail from "./CustomCursor";
import SystemInfoOverlay from "./SystemInfoOverlay";
import SocialLogos from "./Socials";
import CountdownToDate from "./countDown";
import NeonFlickerCircles from "./flickeringCircle";
import Competitions from "./Competitions";
import CursorSound from "./CursorSound";
import RotatedText from "./RotatedText";
import Copyright from "./CopyRight";
import AboutBTUI from "./About";
import ContactBTUI from "./ContactBTUI";
import LeaderboardPage from "./LeaderboardPage";
import ClickSound from "./ClickSound";
import MerchandisePage from "./MerchandisePage";
import RSVPPage from "./RSVPPage";

// ---------------- Label ----------------
function Label({ position, text, onClick, isVisible }) {
  const labelRef = useRef();
  const { camera } = useThree();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useFrame(() => {
    if (labelRef.current) labelRef.current.lookAt(camera.position);
  });

  useEffect(() => {
    if (labelRef.current) {
      labelRef.current.updateWorldMatrix(true, true);
      labelRef.current.lookAt(camera.position);
    }
  }, [camera]);

  if (!isVisible) return null;

  const verticalText = text.split("").map((char, i) => <div key={i}>{char}</div>);

  return (
    <mesh ref={labelRef} position={position}>
      <Html center>
        <div
          onClick={onClick}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            const left = e.currentTarget.querySelector(".left-svg");
            const right = e.currentTarget.querySelector(".right-svg");
            left.style.transform = "translateX(-8px)";
            right.style.transform = "scaleX(-1) translateX(-8px)";
          }}
          onMouseLeave={(e) => {
            const left = e.currentTarget.querySelector(".left-svg");
            const right = e.currentTarget.querySelector(".right-svg");
            left.style.transform = "translateX(0)";
            right.style.transform = "scaleX(-1) translateX(0)";
          }}
        >
          <div
            style={{
              color: "white",
              fontSize: isMobile ? "14px" : "18px",
              fontFamily: "'Courier New', monospace",
              textTransform: "uppercase",
              letterSpacing: "2px",
              fontWeight: "700",
              lineHeight: "1.2",
              textAlign: "center",
            }}
          >
            {verticalText}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "50px" }}>
            <svg
              className="left-svg"
              width="6"
              height="238"
              viewBox="0 0 6 238"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ transition: "transform 0.3s ease", color: "#edfef9" }}
            >
              <path
                d="M5.21093 1.38184L1.16414 1.38184L1.16414 48.858M5.21093 236.618L1.16413 236.618L1.16413 189.572M1.16413 189.572L5.21093 189.572M1.16413 189.572L1.16414 142.527M1.16414 142.527L5.21093 142.527M1.16414 142.527L1.16414 95.6621M1.16414 95.6621L5.21093 95.6621M1.16414 95.6621L1.16414 48.858M1.16414 48.858L5.21093 48.858"
                stroke="currentColor"
              />
            </svg>

            <svg
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ color: "#edfef9" }}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M23.3203 12.9766L0.101562 12.9766L0.101562 11.9766L23.3203 11.9766L23.3203 12.9766Z"
                fill="currentColor"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.2109 24.0859L11.2109 0.867187L12.2109 0.867188L12.2109 24.0859L11.2109 24.0859Z"
                fill="currentColor"
              />
            </svg>

            <svg
              className="right-svg"
              width="6"
              height="238"
              viewBox="0 0 6 238"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                transition: "transform 0.3s ease",
                color: "#edfef9",
                transform: "scaleX(-1) translateX(0)",
              }}
            >
              <path
                d="M5.21093 1.38184L1.16414 1.38184L1.16414 48.858M5.21093 236.618L1.16413 236.618L1.16413 189.572M1.16413 189.572L5.21093 189.572M1.16413 189.572L1.16414 142.527M1.16414 142.527L5.21093 142.527M1.16414 142.527L1.16414 95.6621M1.16414 95.6621L5.21093 95.6621M1.16414 95.6621L1.16414 48.858M1.16414 48.858L5.21093 48.858"
                stroke="currentColor"
              />
            </svg>
          </div>
        </div>
      </Html>
    </mesh>
  );
}

// ---------------- HintText ----------------
function HintText({ position, isVisible }) {
  const labelRef = useRef();
  const { camera } = useThree();

  useFrame(() => {
    if (labelRef.current) labelRef.current.lookAt(camera.position);
  });

  if (!isVisible) return null;

  return (
    <mesh ref={labelRef} position={position}>
      <Html center>
        <div
          style={{
            width: "120px",
            height: "120px",
            borderRadius: "50%",
            background: "rgba(0, 0, 0, 0.25)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            color: "white",
            fontFamily: "'Courier New', monospace",
            textAlign: "center",
            fontWeight: "600",
            fontSize: "12px",
            letterSpacing: "1px",
            textTransform: "uppercase",
            border: "2px solid rgba(255, 255, 255, 0.3)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            cursor: "default",
            transition: "all 0.3s ease",
          }}
        >
          <span style={{ fontSize: "10px", marginBottom: "4px" }}>Click & Drag</span>
          <span style={{ fontSize: "10px" }}>Look Around</span>
        </div>
      </Html>
    </mesh>
  );
}

// ---------------- PanoramaContent ----------------
function PanoramaContent({ textureUrl, controlsRef, onLabelClick, currentPage }) {
  const texture = useMemo(() => {
    const tex = new THREE.TextureLoader().load(textureUrl);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    // Flip horizontally to correct inverted panorama direction.
    tex.repeat.set(-1, 1);
    tex.offset.set(1, 0);
    return tex;
  }, [textureUrl]);
  const [showHint, setShowHint] = useState(true);

  // Auto rotate once
  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls) return;

    let frame = 0;
    const totalFrames = 60; // ~1s full rotation at 60fps
    const startRotation = controls.getAzimuthalAngle();
    const fullCircle = Math.PI * 2;

    const animate = () => {
      if (frame <= totalFrames) {
        const t = frame / totalFrames;
        controls.setAzimuthalAngle(startRotation + fullCircle * t);
        controls.update();
        frame++;
        requestAnimationFrame(animate);
      }
    };
    animate();
  }, [controlsRef]);

  // hide hint on user interaction
  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls) return;

    const handleStart = () => setShowHint(false);
    controls.addEventListener("start", handleStart);
    return () => controls.removeEventListener("start", handleStart);
  }, [controlsRef]);

  const labels = [
    { text: "ABOUT", position: [8, -2, 10], route: "ABOUT" },
    { text: "COMPETITIONS", position: [-12 , 0, -10], route: "COMPETITIONS" },
    { text: "CONTACT", position: [13, 0, -10], route: "CONTACT" },
  ];

  const showLabels = currentPage === "HOME";
  const panoramaWidth = 7680;
  const panoramaHeight = 1080;
  const radius = 20;
  const height = (2 * Math.PI * radius * panoramaHeight) / panoramaWidth; // keep pano aspect on cylinder
  const radialSegments = 120;

  return (
    <>
      <OrbitControls
        ref={controlsRef}
        enableZoom={false}
        enablePan={false}
        minPolarAngle={Math.PI / 2}
        maxPolarAngle={Math.PI / 2}
        enableDamping
        dampingFactor={0.1}
        rotateSpeed={-1}
      />

      <mesh rotation={[0, Math.PI / -1.36, 0]}>
        <cylinderGeometry args={[radius, radius, height, radialSegments, 1, true]} />
        <meshBasicMaterial map={texture} side={THREE.BackSide} />

        {labels.map((lbl, idx) => (
          <Label
            key={idx}
            position={lbl.position}
            isVisible={showLabels}
            text={lbl.text}
            onClick={() => onLabelClick(lbl.route)}
          />
        ))}

        {showHint && <HintText position={[-11.5, .3, 10]} isVisible={showLabels} />}
      </mesh>
    </>
  );
}

// ---------------- PanoramaSphere ----------------
function PanoramaSphere({ textureUrl, onLabelClick, currentPage, controlsRef }) {
  return (
    <Canvas className="panorama-canvas" camera={{ fov: 40 }}>
      <PanoramaContent
        textureUrl={textureUrl}
        controlsRef={controlsRef}
        onLabelClick={onLabelClick}
        currentPage={currentPage}
      />
    </Canvas>
  );
}

// ---------------- Main App ----------------
export default function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const controlsRef = useRef(); // ✅ top-level hook

  const [loading, setLoading] = useState(true);
  const [panoramaTexture, setPanoramaTexture] = useState(null);
  const [currentNav, setCurrentNav] = useState(getCurrentNav());

  // preload panorama
  useEffect(() => {
    new THREE.TextureLoader().load("/panaroma-111.png", (tex) => {
      setPanoramaTexture(tex);
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => setCurrentNav(getCurrentNav()), [location.pathname]);

  function getCurrentNav() {
    if (location.pathname === "/about") return "ABOUT";
    if (location.pathname.startsWith("/competitions")) return "COMPETITIONS";
    if (location.pathname === "/contact") return "CONTACT";
    if (location.pathname === "/leaderboard") return "LEADERBOARD";
    if (location.pathname === "/merchandise" || location.pathname === "/order" || location.pathname === "/orders") return "MERCHANDISE";
    if (location.pathname === "/rsvp") return "RSVP";
    return "HOME";
  }

  const handleNavClick = (nav) => {
    if (nav === currentNav) return;

    setCurrentNav(nav);
    if (nav === "HOME") navigate("/");
    else if (nav === "ABOUT") navigate("/about");
    else if (nav === "CONTACT") navigate("/contact");
    else if (nav === "COMPETITIONS") navigate("/competitions");
    else if (nav === "LEADERBOARD") navigate("/leaderboard");
    else if (nav === "MERCHANDISE") navigate("/merchandise");
    else if (nav === "RSVP") navigate("/rsvp");
  };

  return (
    <div className="app-container" data-current-page={currentNav.toLowerCase()}>
      {/* panorama background */}
      {panoramaTexture && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: -1,
            opacity: loading ? 0.2 : 1,
            transition: "opacity 1s ease",
          }}
        >
          <PanoramaSphere
            textureUrl="/panaroma-111.png"
            onLabelClick={handleNavClick}
            currentPage={currentNav}
            controlsRef={controlsRef}
          />
        </div>
      )}

      {/* loader overlay */}
      {loading && <Loader />}

      {/* main UI */}
      {!loading && (
        <>
          <CursorSound soundSrc="/hover.mp3" enabled={true} />
          <ClickSound soundSrc="/cursor-sound3.wav" enabled={true} />

          <RotatedText />
          <Copyright />
          <SocialLogos />
          <CoolCursorTrail />
          <BackgroundSVGs />
          <LogoDisplay />
          <TopNavigation onNavClick={handleNavClick} />

          <div className="main-content">
            {currentNav === "HOME" && (
              <>
                <TerminalFeed />
                <p className="left-side-menu-title">Sponsors</p>
                <SideMenu />
                <CountdownToDate targetDate="2025-11-11T23:59:59" title="Registratons Closing" />
                <SystemInfoOverlay />
                <FooterInfo />
              </>
            )}
            {currentNav === "COMPETITIONS" && <Competitions />}
            {currentNav === "ABOUT" && <AboutBTUI />}
            {currentNav === "CONTACT" && <ContactBTUI />}
            {currentNav === "LEADERBOARD" && <LeaderboardPage />}
            {currentNav === "MERCHANDISE" && <MerchandisePage />}
            {currentNav === "RSVP" && <RSVPPage />}
          </div>

          <div className="waveform-wrapper">
            <WaveformPlayer audioSrc="/music.mp3" onPlayStateChange={() => {}} />
          </div>
          <div className="radar-wrapper">
            <Radar size={55} />
          </div>
          <NeonFlickerCircles />
        </>
      )}
    </div>
  );
}
